import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import './SelectorFiltros.css';

/**
 * Selector de filtros visibles. Dropdown renderizado con Portal en body para evitar quedar detrás de la tabla.
 */
export function SelectorFiltros({
  filtros,
  visibleIds,
  onToggle,
  onRestaurar,
  onSave,
  titulo = 'Filtros visibles',
  labelBoton = 'Filtros',
}) {
  const [abierto, setAbierto] = useState(false);
  const [localVisibleIds, setLocalVisibleIds] = useState([]);
  const [dropdownRect, setDropdownRect] = useState(null);
  const ref = useRef(null);

  useEffect(() => {
    if (abierto) setLocalVisibleIds([...visibleIds]);
  }, [abierto, visibleIds]);

  const updatePosition = () => {
    if (ref.current) {
      const rect = ref.current.getBoundingClientRect();
      setDropdownRect({ top: rect.bottom + 4, left: rect.left });
    }
  };

  useEffect(() => {
    if (abierto) {
      updatePosition();
      const onScrollOrResize = () => updatePosition();
      window.addEventListener('scroll', onScrollOrResize, true);
      window.addEventListener('resize', onScrollOrResize);
      return () => {
        window.removeEventListener('scroll', onScrollOrResize, true);
        window.removeEventListener('resize', onScrollOrResize);
      };
    } else {
      setDropdownRect(null);
    }
  }, [abierto]);

  const buildSnapshot = () => {
    const filtrosVisibles = {};
    filtros.forEach((f) => {
      filtrosVisibles[f.id] = localVisibleIds.includes(f.id);
    });
    return { filtrosVisibles };
  };

  const syncParentFromLocal = () => {
    filtros.forEach((f) => {
      const inLocal = localVisibleIds.includes(f.id);
      const inParent = visibleIds.includes(f.id);
      if (inLocal !== inParent) onToggle(f.id);
    });
  };

  const handleCerrar = () => {
    syncParentFromLocal();
    const snapshot = buildSnapshot();
    setAbierto(false);
    if (onSave) onSave(snapshot);
  };

  const handleLocalToggle = (id) => {
    setLocalVisibleIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const handleToggleBoton = () => {
    if (abierto) {
      syncParentFromLocal();
      if (onSave) onSave(buildSnapshot());
    }
    setAbierto((o) => !o);
  };

  const handleRestaurar = () => {
    onRestaurar();
    setAbierto(false);
    if (onSave) onSave();
  };

  const dropdownContent = abierto && dropdownRect && (
    <div
      className="selector-filtros-dropdown"
      role="dialog"
      aria-label={titulo}
      style={{
        position: 'fixed',
        top: dropdownRect.top,
        left: dropdownRect.left,
        width: 'max-content',
        minWidth: 'max-content',
        zIndex: 10000,
      }}
    >
      <div className="selector-filtros-header">
        <span>{titulo}</span>
        <button type="button" className="selector-filtros-restaurar" onClick={handleRestaurar}>
          Ver todos
        </button>
      </div>
      <ul className="selector-filtros-lista">
        {filtros.map((f) => (
          <li key={f.id}>
            <label className="selector-filtros-label">
              <input
                type="checkbox"
                checked={localVisibleIds.includes(f.id)}
                onChange={() => handleLocalToggle(f.id)}
              />
              <span>{f.label}</span>
            </label>
          </li>
        ))}
      </ul>
      <div className="selector-filtros-footer">
        <button type="button" className="selector-filtros-cerrar" onClick={handleCerrar}>
          Listo
        </button>
      </div>
    </div>
  );

  return (
    <>
      <div className="selector-filtros" ref={ref}>
        <button
          type="button"
          className="btn-selector-filtros"
          onClick={handleToggleBoton}
          title="Seleccionar filtros visibles"
          aria-expanded={abierto}
          aria-haspopup="true"
        >
          🔍 {labelBoton}
        </button>
      </div>
      {dropdownContent && createPortal(dropdownContent, document.body)}
    </>
  );
}
